/**
 * UC-O4 Annual revenue seasonality — logic ported from ExecutiveDashboard
 * `renderRevenueSeasonalityWidget`.
 */

export interface RevenueSeasonalityRow {
  calendar_year?: number
  financial_year?: number
  revenue_year?: number
  revenue_month: number
  monthly_revenue?: string | number
  fy_label?: string
  quarter?: string
  season?: string
  seasonal_category?: string
  yoy_change_percent?: number
}

export interface SeasonalPatternRow {
  month: number
  monthName: string
  monthShort: string
  fyMonthOrder: number
  avgRevenue: number
  totalRevenue: number
  count: number
  bestYear: number | null
  worstYear: number | null
  bestRevenue: number
  worstRevenue: number
}

export interface YearComparisonRow {
  year: number
  fyLabel: string
  totalRevenue: number
  avgPerMonth: number
  monthCount: number
  yoyPct: number | null
}

export function num(v: string | number | undefined): number {
  if (v === undefined || v === null) return 0
  const n = typeof v === 'number' ? v : parseFloat(v)
  return Number.isFinite(n) ? n : 0
}

export function getIndianFYMonthOrder(month: number): number {
  if (month >= 4) return month - 3
  return month + 9
}

export function rowFY(item: RevenueSeasonalityRow): number {
  return parseInt(
    String(item.financial_year ?? item.revenue_year ?? 0),
    10
  )
}

export function sortSeasonalityRows(
  rows: RevenueSeasonalityRow[]
): RevenueSeasonalityRow[] {
  return [...rows].sort((a, b) => {
    const yearA = rowFY(a)
    const yearB = rowFY(b)
    const monthA = parseInt(String(a.revenue_month || 0), 10)
    const monthB = parseInt(String(b.revenue_month || 0), 10)
    if (yearA !== yearB) return yearA - yearB
    return getIndianFYMonthOrder(monthA) - getIndianFYMonthOrder(monthB)
  })
}

export function buildFyLabelMap(
  rows: RevenueSeasonalityRow[]
): Record<number, string> {
  const map: Record<number, string> = {}
  for (const item of rows) {
    const fy = rowFY(item)
    if (fy > 0 && !map[fy]) {
      map[fy] =
        item.fy_label?.trim() ||
        `FY ${fy}-${String((fy + 1) % 100).padStart(2, '0')}`
    }
  }
  return map
}

export function allFinancialYearsSorted(
  rows: RevenueSeasonalityRow[]
): number[] {
  const set = new Set<number>()
  for (const item of rows) {
    const fy = rowFY(item)
    if (fy > 0) set.add(fy)
  }
  return [...set].sort((a, b) => a - b)
}

export function filterByYears(
  rows: RevenueSeasonalityRow[],
  years: number[]
): RevenueSeasonalityRow[] {
  const yset = new Set(years)
  return rows.filter(item => yset.has(rowFY(item)))
}

export function buildSeasonalPattern(
  filtered: RevenueSeasonalityRow[],
  selectedYears: number[]
): SeasonalPatternRow[] {
  const monthlyAverages: Record<number, { total: number; count: number }> = {}
  for (const item of filtered) {
    const month = parseInt(String(item.revenue_month || 0), 10)
    if (!monthlyAverages[month]) {
      monthlyAverages[month] = { total: 0, count: 0 }
    }
    monthlyAverages[month].total += num(item.monthly_revenue)
    monthlyAverages[month].count += 1
  }

  const multi = selectedYears.length > 1

  return Object.entries(monthlyAverages)
    .map(([monthStr, data]) => {
      const monthInt = parseInt(monthStr, 10)
      let bestYear: number | null = null
      let worstYear: number | null = null
      let bestRevenue = 0
      let worstRevenue = Infinity

      if (multi) {
        const monthData = filtered.filter(
          item => parseInt(String(item.revenue_month), 10) === monthInt
        )
        for (const item of monthData) {
          const revenue = num(item.monthly_revenue)
          const y = rowFY(item)
          if (revenue > bestRevenue) {
            bestRevenue = revenue
            bestYear = y
          }
          if (revenue < worstRevenue) {
            worstRevenue = revenue
            worstYear = y
          }
        }
      }

      return {
        month: monthInt,
        monthName: new Date(2020, monthInt - 1).toLocaleDateString('en-US', {
          month: 'long',
        }),
        monthShort: new Date(2020, monthInt - 1).toLocaleDateString('en-US', {
          month: 'short',
        }),
        fyMonthOrder: getIndianFYMonthOrder(monthInt),
        avgRevenue: data.total / data.count,
        totalRevenue: data.total,
        count: data.count,
        bestYear,
        worstYear,
        bestRevenue,
        worstRevenue: worstRevenue === Infinity ? 0 : worstRevenue,
      }
    })
    .sort((a, b) => a.fyMonthOrder - b.fyMonthOrder)
}

export function seasonalVariancePercent(
  pattern: SeasonalPatternRow[],
  avgMonthlyRevenue: number
): number {
  if (!pattern.length || avgMonthlyRevenue <= 0) return 0
  const avgs = pattern.map(m => m.avgRevenue)
  const peak = Math.max(...avgs)
  const trough = Math.min(...avgs)
  return ((peak - trough) / avgMonthlyRevenue) * 100
}

export function peakAndChallengingMonths(pattern: SeasonalPatternRow[]): {
  top3: SeasonalPatternRow[]
  bottom3: SeasonalPatternRow[]
} {
  const sortedByRevenue = [...pattern].sort(
    (a, b) => b.avgRevenue - a.avgRevenue
  )
  const n = sortedByRevenue.length
  const top3 = sortedByRevenue.slice(0, Math.min(3, n))
  const bottom3 = sortedByRevenue
    .slice(-Math.min(3, n))
    .reverse()
  return { top3, bottom3 }
}

export function buildYearComparisonRows(
  filtered: RevenueSeasonalityRow[],
  sortedAll: RevenueSeasonalityRow[],
  selectedYears: number[],
  fyLabelMap: Record<number, string>
): YearComparisonRow[] {
  const allFY = allFinancialYearsSorted(sortedAll)
  const years = [...selectedYears].sort((a, b) => a - b)

  return years.map(year => {
    const yearData = filtered.filter(item => rowFY(item) === year)
    const totalRevenue = yearData.reduce(
      (s, item) => s + num(item.monthly_revenue),
      0
    )
    const monthCount = yearData.length
    const avgPerMonth = monthCount > 0 ? totalRevenue / monthCount : 0

    let yoyPct: number | null = null
    const yearIndex = allFY.indexOf(year)
    if (yearIndex > 0) {
      const prev = allFY[yearIndex - 1]
      const prevData = sortedAll.filter(item => rowFY(item) === prev)
      const prevTotal = prevData.reduce(
        (s, item) => s + num(item.monthly_revenue),
        0
      )
      if (prevTotal > 0) {
        yoyPct = ((totalRevenue - prevTotal) / prevTotal) * 100
      }
    }

    return {
      year,
      fyLabel: fyLabelMap[year] || `FY ${year}`,
      totalRevenue,
      avgPerMonth,
      monthCount,
      yoyPct,
    }
  })
}

/** Indian FY month sequence Apr → Mar; each row has `rev_${fy}` for multi-line chart. */
export function buildMultiYearMonthlyComparisonChart(
  filtered: RevenueSeasonalityRow[],
  selectedYears: number[]
): {
  rows: Record<string, unknown>[]
  sortedYears: number[]
} {
  const INDIAN_FY_MONTHS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3]
  const sortedYears = [...selectedYears].sort((a, b) => a - b)

  const mapKey = (fy: number, cm: number) => `${fy}:${cm}`
  const revMap = new Map<string, number>()
  for (const item of filtered) {
    const fy = rowFY(item)
    const cm = parseInt(String(item.revenue_month || 0), 10)
    if (!cm) continue
    revMap.set(mapKey(fy, cm), num(item.monthly_revenue))
  }

  const rows = INDIAN_FY_MONTHS.map(cm => {
    const row: Record<string, unknown> = {
      revenue_month: cm,
      monthShort: new Date(2020, cm - 1).toLocaleDateString('en-US', {
        month: 'short',
      }),
      monthName: new Date(2020, cm - 1).toLocaleDateString('en-US', {
        month: 'long',
      }),
      fyMonthOrder: getIndianFYMonthOrder(cm),
    }
    for (const y of sortedYears) {
      const v = revMap.get(mapKey(y, cm))
      row[`rev_${y}`] = v !== undefined ? v : null
    }
    return row
  })

  return { rows, sortedYears }
}
